// src/services/CustomerSatisfactionService.js

class CustomerSatisfactionService {
  constructor() {
    // Positive keywords and phrases
    this.positiveKeywords = [
      'excellent', 'amazing', 'great', 'good', 'fantastic', 'wonderful',
      'awesome', 'outstanding', 'exceptional', 'superb', 'brilliant',
      'perfect', 'lovely', 'beautiful', 'impressive', 'incredible',
      'best', 'favorite', 'love', 'like', 'recommend', 'satisfied',
      'happy', 'pleased', 'delighted', 'thankful', 'grateful',
      'helpful', 'friendly', 'professional', 'knowledgeable',
      'efficient', 'quick', 'fast', 'reliable', 'trustworthy',
      'clean', 'organized', 'responsive', 'caring', 'compassionate'
    ];

    this.negativeKeywords = [
      'bad', 'terrible', 'awful', 'horrible', 'poor', 'worst',
      'disappointed', 'unhappy', 'dissatisfied', 'frustrated',
      'annoyed', 'angry', 'upset', 'hate', 'dislike',
      'unhelpful', 'rude', 'unprofessional', 'incompetent',
      'slow', 'unreliable', 'dirty', 'messy', 'unresponsive',
      'ignorant', 'careless', 'inconsiderate'
    ];

    // Weighted scoring system
    this.sentimentWeights = {
      excellent: 10,
      amazing: 9,
      fantastic: 9,
      wonderful: 9,
      great: 8,
      good: 7,
      satisfied: 7,
      happy: 7,
      recommend: 8,
      love: 9,
      like: 6,
      helpful: 7,
      professional: 8,
      friendly: 7,
      best: 9,
      perfect: 10,
      outstanding: 9,
      superb: 9,
      brilliant: 9,
      impressive: 8,
      incredible: 9,
      awesome: 9,
      lovely: 7,
      beautiful: 7,
      favorite: 8,
      pleased: 7,
      delighted: 8,
      thankful: 7,
      grateful: 7,
      knowledgeable: 8,
      efficient: 8,
      quick: 7,
      fast: 7,
      reliable: 8,
      trustworthy: 8,
      clean: 7,
      organized: 7,
      responsive: 7,
      caring: 8,
      compassionate: 8
    };
  }

  /**
   * Analyze a single review for sentiment
   * @param {string} reviewText - The review text to analyze
   * @param {number} rating - Optional rating (1-5 stars)
   * @returns {Object} Analysis result
   */
  analyzeReview(reviewText, rating = null) {
    if (!reviewText || reviewText.trim().length === 0) {
      return {
        sentiment: 'neutral',
        score: 0,
        confidence: 0,
        positiveCount: 0,
        negativeCount: 0,
        isPositive: false,
        isNegative: false,
        isNeutral: true,
        rating: rating || null
      };
    }

    const text = reviewText.toLowerCase();
    const words = text.split(/\s+/);
    
    let positiveCount = 0;
    let negativeCount = 0;
    let sentimentScore = 0;
    let totalWeight = 0;

    // Check for positive keywords
    this.positiveKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      const matches = text.match(regex);
      if (matches) {
        const weight = this.sentimentWeights[keyword] || 5;
        positiveCount += matches.length;
        sentimentScore += matches.length * weight;
        totalWeight += matches.length;
      }
    });

    // Check for negative keywords
    this.negativeKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'g');
      const matches = text.match(regex);
      if (matches) {
        negativeCount += matches.length;
        sentimentScore -= matches.length * 5;
        totalWeight += matches.length;
      }
    });

    // Calculate sentiment score (0-100 scale)
    let normalizedScore = 0;
    if (totalWeight > 0) {
      normalizedScore = (sentimentScore / totalWeight) * 10;
      normalizedScore = Math.max(0, Math.min(100, normalizedScore));
    } else {
      // If no keywords found, use neutral
      normalizedScore = 50;
    }

    // If rating is provided, use it to adjust the score
    if (rating) {
      const ratingScore = (rating / 5) * 100;
      // Weighted average: 60% text analysis, 40% rating
      normalizedScore = (normalizedScore * 0.6) + (ratingScore * 0.4);
    }

    // Determine sentiment
    let sentiment = 'neutral';
    let isPositive = false;
    let isNegative = false;
    let isNeutral = true;

    if (normalizedScore >= 70) {
      sentiment = 'positive';
      isPositive = true;
      isNeutral = false;
    } else if (normalizedScore <= 30) {
      sentiment = 'negative';
      isNegative = true;
      isNeutral = false;
    }

    // Calculate confidence based on keyword matches
    const confidence = Math.min(100, (totalWeight / 5) * 20);

    return {
      sentiment,
      score: Math.round(normalizedScore),
      confidence: Math.round(confidence),
      positiveCount,
      negativeCount,
      isPositive,
      isNegative,
      isNeutral,
      rating: rating || null,
      totalKeywords: totalWeight
    };
  }

  /**
   * Analyze multiple reviews and generate satisfaction metrics
   * @param {Array} reviews - Array of review objects with text and optional rating
   * @returns {Object} Satisfaction metrics
   */
  analyzeReviews(reviews) {
    if (!reviews || reviews.length === 0) {
      return this.getEmptyMetrics();
    }

    const results = reviews.map(review => {
      const text = review.text || review.reviewText || review.comment || '';
      const rating = review.rating || review.stars || null;
      return this.analyzeReview(text, rating);
    });

    const totalReviews = results.length;
    const positiveReviews = results.filter(r => r.isPositive);
    const negativeReviews = results.filter(r => r.isNegative);
    const neutralReviews = results.filter(r => r.isNeutral);

    const positiveCount = positiveReviews.length;
    const negativeCount = negativeReviews.length;
    const neutralCount = neutralReviews.length;

    // Calculate percentages
    const positivePercentage = (positiveCount / totalReviews) * 100;
    const negativePercentage = (negativeCount / totalReviews) * 100;
    const neutralPercentage = (neutralCount / totalReviews) * 100;

    // Calculate average sentiment score
    const averageScore = results.reduce((sum, r) => sum + r.score, 0) / totalReviews;

    // Calculate satisfaction rate (positive + 50% of neutral)
    const satisfactionRate = positivePercentage + (neutralPercentage * 0.5);

    // Get most common positive and negative keywords
    const allPositiveKeywords = [];
    const allNegativeKeywords = [];
    reviews.forEach(review => {
      const text = review.text || review.reviewText || review.comment || '';
      const lowerText = text.toLowerCase();
      
      this.positiveKeywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          allPositiveKeywords.push(keyword);
        }
      });
      
      this.negativeKeywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          allNegativeKeywords.push(keyword);
        }
      });
    });

    const getTopKeywords = (keywords, limit = 5) => {
      const counts = {};
      keywords.forEach(k => {
        counts[k] = (counts[k] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([keyword, count]) => ({ keyword, count }));
    };

    return {
      totalReviews,
      positiveCount,
      negativeCount,
      neutralCount,
      positivePercentage: Math.round(positivePercentage),
      negativePercentage: Math.round(negativePercentage),
      neutralPercentage: Math.round(neutralPercentage),
      averageScore: Math.round(averageScore),
      satisfactionRate: Math.round(satisfactionRate),
      overallSentiment: this.getOverallSentiment(positivePercentage, negativePercentage),
      topPositiveKeywords: getTopKeywords(allPositiveKeywords),
      topNegativeKeywords: getTopKeywords(allNegativeKeywords),
      reviews: results,
      // Summary text
      summary: this.generateSummary(positivePercentage, negativePercentage, totalReviews),
      // For charts and visualizations
      metrics: {
        satisfaction: Math.round(satisfactionRate),
        positive: Math.round(positivePercentage),
        negative: Math.round(negativePercentage),
        neutral: Math.round(neutralPercentage),
        averageScore: Math.round(averageScore)
      },
      // Individual review breakdown
      reviewBreakdown: {
        positive: positiveCount,
        negative: negativeCount,
        neutral: neutralCount
      }
    };
  }

  /**
   * Get overall sentiment based on percentages
   */
  getOverallSentiment(positive, negative) {
    if (positive >= 70) return 'Excellent';
    if (positive >= 50) return 'Good';
    if (positive >= 30) return 'Average';
    if (negative >= 70) return 'Poor';
    if (negative >= 50) return 'Needs Improvement';
    return 'Mixed';
  }

  /**
   * Generate a summary text
   */
  generateSummary(positive, negative, total) {
    if (total === 0) return 'No reviews available.';
    
    if (positive >= 70) {
      return `Excellent customer satisfaction! ${Math.round(positive)}% of customers are happy with the service.`;
    } else if (positive >= 50) {
      return `Good customer satisfaction. ${Math.round(positive)}% of customers are satisfied with the service.`;
    } else if (positive >= 30) {
      return `Average customer satisfaction. There is room for improvement.`;
    } else if (negative >= 70) {
      return `Poor customer satisfaction. Immediate attention is needed to improve service quality.`;
    } else if (negative >= 50) {
      return `Customer satisfaction needs improvement. Focus on addressing common complaints.`;
    } else {
      return `Mixed customer feedback. Continue monitoring and improving service quality.`;
    }
  }

  /**
   * Get empty metrics
   */
  getEmptyMetrics() {
    return {
      totalReviews: 0,
      positiveCount: 0,
      negativeCount: 0,
      neutralCount: 0,
      positivePercentage: 0,
      negativePercentage: 0,
      neutralPercentage: 0,
      averageScore: 0,
      satisfactionRate: 0,
      overallSentiment: 'No Data',
      topPositiveKeywords: [],
      topNegativeKeywords: [],
      reviews: [],
      summary: 'No reviews available to analyze.',
      metrics: {
        satisfaction: 0,
        positive: 0,
        negative: 0,
        neutral: 0,
        averageScore: 0
      },
      reviewBreakdown: {
        positive: 0,
        negative: 0,
        neutral: 0
      }
    };
  }

  /**
   * Calculate satisfaction trend over time
   */
  calculateTrend(reviews, dateField = 'createdAt') {
    if (!reviews || reviews.length === 0) return [];

    const sorted = [...reviews].sort((a, b) => 
      new Date(a[dateField]) - new Date(b[dateField])
    );

    const groupedByDate = {};
    sorted.forEach(review => {
      const date = new Date(review[dateField]);
      const key = date.toISOString().split('T')[0];
      if (!groupedByDate[key]) {
        groupedByDate[key] = [];
      }
      groupedByDate[key].push(review);
    });

    const trend = Object.entries(groupedByDate).map(([date, dayReviews]) => {
      const metrics = this.analyzeReviews(dayReviews);
      return {
        date,
        satisfaction: metrics.satisfactionRate,
        positive: metrics.positivePercentage,
        negative: metrics.negativePercentage,
        neutral: metrics.neutralPercentage,
        count: dayReviews.length
      };
    });

    return trend;
  }
}

// Create and export a singleton instance
const customerSatisfactionService = new CustomerSatisfactionService();
export default customerSatisfactionService;